// src/utils/AuthContext.js
// MAPAPET_MESSENGER_STAGE2

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] =
    useState(false);

  const authRevisionRef = useRef(0);

  useEffect(() => {
    let disposed = false;
    let unsubscribe = null;
    let startTimer = null;

    const startAuth = async () => {
      try {
        const [
          { auth },
          { onAuthStateChanged },
        ] = await Promise.all([
          import("../firebaseAuthClient"),
          import("firebase/auth"),
        ]);

        if (disposed) return;

        unsubscribe = onAuthStateChanged(
          auth,
          async (firebaseUser) => {
            if (disposed) return;

            const authRevision =
              authRevisionRef.current + 1;

            authRevisionRef.current =
              authRevision;

            if (!firebaseUser) {
              setUser(null);
              setProfileLoading(false);
              setLoading(false);
              return;
            }

            /*
             * Firebase Auth уже подтвердил пользователя.
             * Не удерживаем весь интерфейс, пока отдельно читаются
             * публичный и приватный профили Firestore.
             */
            const baseUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || "",
              displayName:
                firebaseUser.displayName || "",
              avatar:
                firebaseUser.photoURL ||
                "/img/default-avatar.svg",
            };

            setUser(baseUser);
            setLoading(false);
            setProfileLoading(true);

            try {
              const [
                { db },
                { doc, getDoc },
              ] = await Promise.all([
                import("../firebaseDbClient"),
                import("firebase/firestore"),
              ]);

              if (
                disposed ||
                authRevisionRef.current !==
                  authRevision
              ) {
                return;
              }

              const [
                publicSnapshot,
                privateSnapshot,
              ] = await Promise.all([
                getDoc(
                  doc(
                    db,
                    "users",
                    firebaseUser.uid
                  )
                ),
                getDoc(
                  doc(
                    db,
                    "userPrivate",
                    firebaseUser.uid
                  )
                ),
              ]);

              if (
                disposed ||
                authRevisionRef.current !==
                  authRevision
              ) {
                return;
              }

              const publicProfile =
                publicSnapshot.exists()
                  ? publicSnapshot.data() || {}
                  : {};

              const privateProfile =
                privateSnapshot.exists()
                  ? privateSnapshot.data() || {}
                  : {};

              setUser({
                ...baseUser,
                ...publicProfile,
                ...privateProfile,
                uid: firebaseUser.uid,
                email:
                  firebaseUser.email ||
                  privateProfile.email ||
                  "",
              });
            } catch (error) {
              console.error(
                "Ошибка загрузки профиля:",
                error
              );
            } finally {
              if (
                !disposed &&
                authRevisionRef.current ===
                  authRevision
              ) {
                setProfileLoading(false);
              }
            }
          }
        );
      } catch (error) {
        console.error(
          "Не удалось запустить авторизацию:",
          error
        );

        if (!disposed) {
          setLoading(false);
        }
      }
    };

    /*
     * React сначала рисует страницу и LCP-постер. Firebase Auth
     * подключается следующим коротким заданием, а не удерживает
     * весь интерфейс в пустом состоянии на медленном телефоне.
     */
    startTimer = window.setTimeout(startAuth, 0);

    return () => {
      disposed = true;
      window.clearTimeout(startTimer);
      unsubscribe?.();
    };
  }, []);

  /*
   * Presence сначала работает через Realtime Database.
   * Если URL базы ещё не настроен, временно остаётся прежний
   * Firestore fallback, поэтому выкладка не ломает мессенджер.
   */
  useEffect(() => {
    const userId = user?.uid;

    if (!userId) return undefined;

    let disposed = false;
    let cleanupPresence = null;

    const startLegacyFirestorePresence =
      async () => {
        const [
          { db },
          {
            doc,
            setDoc,
            serverTimestamp,
          },
        ] = await Promise.all([
          import("../firebaseDbClient"),
          import("firebase/firestore"),
        ]);

        if (disposed) return null;

        const userRef = doc(
          db,
          "users",
          userId
        );

        let lastWrittenOnline = null;

        const writePresence =
          async (online) => {
            if (
              disposed ||
              lastWrittenOnline === online
            ) {
              return;
            }

            lastWrittenOnline = online;

            const payload = {
              online,
              lastActiveAt:
                serverTimestamp(),
            };

            if (!online) {
              payload.lastSeen =
                serverTimestamp();
            }

            try {
              await setDoc(
                userRef,
                payload,
                {
                  merge: true,
                }
              );
            } catch (error) {
              lastWrittenOnline = null;

              console.warn(
                "Не удалось обновить резервный Firestore presence:",
                error
              );
            }
          };

        const updateByVisibility = () => {
          writePresence(
            document.visibilityState ===
              "visible"
          );
        };

        const markOnline = () =>
          writePresence(true);

        const markOffline = () =>
          writePresence(false);

        updateByVisibility();

        document.addEventListener(
          "visibilitychange",
          updateByVisibility
        );

        window.addEventListener(
          "focus",
          markOnline
        );

        window.addEventListener(
          "blur",
          markOffline
        );

        window.addEventListener(
          "pagehide",
          markOffline
        );

        return () => {
          document.removeEventListener(
            "visibilitychange",
            updateByVisibility
          );

          window.removeEventListener(
            "focus",
            markOnline
          );

          window.removeEventListener(
            "blur",
            markOffline
          );

          window.removeEventListener(
            "pagehide",
            markOffline
          );

          markOffline();
        };
      };

    const startPresence = async () => {
      try {
        const {
          isRealtimeDatabaseConfigured,
          startRealtimePresence,
        } = await import(
          "../services/realtimePresence"
        );

        if (disposed) return;

        if (
          isRealtimeDatabaseConfigured()
        ) {
          const nextCleanup =
            await startRealtimePresence(
              userId
            );

          if (disposed) {
            await nextCleanup?.();
            return;
          }

          cleanupPresence =
            nextCleanup;

          return;
        }

        const nextCleanup =
          await startLegacyFirestorePresence();

        if (disposed) {
          nextCleanup?.();
          return;
        }

        cleanupPresence =
          nextCleanup;
      } catch (error) {
        console.warn(
          "Не удалось подключить RTDB presence, используется Firestore fallback:",
          error
        );

        if (!disposed) {
          const nextCleanup =
            await startLegacyFirestorePresence();

          if (disposed) {
            nextCleanup?.();
            return;
          }

          cleanupPresence =
            nextCleanup;
        }
      }
    };

    const timer = window.setTimeout(
      startPresence,
      250
    );

    return () => {
      disposed = true;
      window.clearTimeout(timer);

      if (
        typeof cleanupPresence ===
        "function"
      ) {
        Promise.resolve(
          cleanupPresence()
        ).catch(() => {});
      }
    };
  }, [user?.uid]);

  const updateUser = (newData) => {
    setUser((previous) =>
      previous
        ? {
            ...previous,
            ...newData,
          }
        : previous
    );
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        updateUser,
        loading,
        authLoading: loading,
        authResolved: !loading,
        profileLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
